import { Link } from "react-router-dom";
import { Clock, CircleCheck, Eye, Trash2, MessageSquareWarning } from "lucide-react";
import { CampaignShareBar } from "../CampaignShareBar";
import { formatDollars } from "../../lib/draftCampaign";
import { ReviewProgressTrack } from "./ReviewProgressTrack";

/** pending_review — open layout: the stepper, the campaign line, the yellow share field. Status lives in the plate's stamp. */
export const ReviewLockedPanel = ({ draft, payoutState }) => {
  const first = (draft.full_name || "").trim().split(/\s+/)[0];
  const goal = formatDollars(draft.goal_cents);
  const shareText = first
    ? `${first} just submitted a My Rich Aunt campaign${draft.school ? ` at ${draft.school}` : ""}. Raising ${goal}. Every gift is tax-deductible.`
    : `I just submitted my My Rich Aunt campaign. Raising ${goal}. Every gift is tax-deductible.`;
  const payoutsDone = payoutState === "ready" || payoutState === "in_review";
  return (
    <div data-testid="builder-locked" className="space-y-14 lg:space-y-16">
      <ReviewProgressTrack enrollmentVerified={Boolean(draft.enrollment_verified)} payoutsDone={payoutsDone} />

      <div className="border-t border-ink/10 pt-10" data-testid="pending-review-banner">
        <p className="font-serif text-3xl leading-tight text-ink">&ldquo;{draft.title}&rdquo;</p>
        <p className="mt-3 max-w-2xl text-base leading-relaxed text-olive">
          {goal} goal{draft.school ? ` · ${draft.school}` : ""}. Your page is locked while it&rsquo;s under review. If anything needs a tweak, we&rsquo;ll send it back with a note and editing reopens.
        </p>
        <Link to={`/campaign/${draft.id}`} target="_blank" rel="noopener noreferrer" data-testid="locked-preview-link" className="mt-5 inline-flex items-center gap-2 rounded-full border border-ink px-5 py-2.5 text-base font-semibold text-ink transition-colors hover:bg-ink hover:text-sand">
          <Eye className="h-4 w-4" strokeWidth={1.75} /> See your page as a donor will
        </Link>
      </div>

      <div data-testid="locked-share" className="rounded-2xl bg-yellow p-7 lg:p-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <h2 className="font-serif text-3xl leading-tight text-ink">Get ready to spread the word.</h2>
            <p className="mt-3 max-w-lg text-base leading-relaxed text-ink/80">The sooner your extended family, roommates, and group chats know the goal, the faster you get funded. Your link works the moment your page is live.</p>
          </div>
          <div className="lg:col-span-5 lg:justify-self-end">
            <CampaignShareBar shareText={shareText} bold />
          </div>
        </div>
      </div>
    </div>
  );
};

/** live/paused — edits queue as pending changes; the public page is unchanged until approved. */
export const PendingChangesNotice = ({ pending, onDiscard }) => {
  if (!pending?.status) return null;
  const rejected = pending.status === "rejected";
  return (
    <div
      data-testid="pending-changes-notice"
      data-state={pending.status}
      className={`flex items-start gap-3 rounded-lg border p-4 ${rejected ? "border-scarlet/40 bg-scarlet/[0.05]" : "border-cobalt/40 bg-cobalt/[0.05]"}`}
    >
      {rejected ? <MessageSquareWarning className="mt-0.5 h-4 w-4 shrink-0 text-scarlet" strokeWidth={2} /> : <Clock className="mt-0.5 h-4 w-4 shrink-0 text-cobalt" strokeWidth={2} />}
      <div className="min-w-0 flex-1">
        <p className={`text-[13px] font-semibold uppercase tracking-[0.22em] ${rejected ? "text-scarlet" : "text-cobalt"}`}>
          {rejected ? "Edits returned with a note" : "Edits awaiting review"}
        </p>
        <p className="mt-1 text-[13px] text-ink">
          {rejected
            ? "Your live page is unchanged. Make the change below and save — it comes straight back to us."
            : "Your live page is unchanged until a reviewer approves these edits."}
        </p>
        {rejected && pending.rejection_reason && (
          <blockquote className="mt-2 rounded-md border-l-4 border-scarlet bg-bone px-3 py-2 font-serif text-base text-ink" data-testid="pending-changes-reason">
            &ldquo;{pending.rejection_reason}&rdquo;
          </blockquote>
        )}
      </div>
      <button type="button" onClick={onDiscard} data-testid="pending-changes-discard" className="inline-flex shrink-0 items-center gap-1 text-[13px] font-semibold text-olive hover:text-scarlet">
        <Trash2 className="h-3.5 w-3.5" /> Discard edits
      </button>
    </div>
  );
};

export const PausedNote = () => (
  <div className="flex items-start gap-3 rounded-lg border border-yellow/70 bg-yellow/[0.15] p-4" data-testid="paused-note">
    <Clock className="mt-0.5 h-4 w-4 shrink-0 text-ink" strokeWidth={2} />
    <p className="text-[13px] text-ink">Your page is <strong>paused by our team</strong> and hidden from donors for the moment. Only you can see it. We&rsquo;ll email you; nothing you&rsquo;ve raised is affected. You can still make edits — they go live with the page.</p>
  </div>
);

export const ApprovedNote = () => (
  <div className="flex items-start gap-3 rounded-lg border border-grass/30 bg-grass/[0.06] p-4" data-testid="live-edit-note">
    <CircleCheck className="mt-0.5 h-4 w-4 shrink-0 text-grass" strokeWidth={2} />
    <p className="text-[13px] text-ink">Your page is live. Anything you change here is reviewed before donors see it; the approved version stays up in the meantime.</p>
  </div>
);
